import React, { useContext } from 'react';
import {
  ApplicationUIContext,
  Drawer,
  DrawerContent,
  Form,
  SectionTitle,
  useDrawerState,
} from '@yuhuinc/ui-library';
import { requiredRule } from '../common/Rules';
import { getTranslatedString } from 'helpers/i18n';
import styled from 'styled-components';
import { Button, Divider, InputNumber, Select, Space } from 'antd';
import { StyledSpace } from '../common/StyledComponents';
import ScheduledEventsTable from './ScheduledEventsTable';
import { getLabel, startingMonthOptions, renderAmountPerMonth } from './common/common';

const doNothing = () => {};

const MIN_DURATION_IN_MONTHS = 1;
const MAX_DURATION_IN_MONTHS = 99;

const StyledRow = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const defaultInitialValues = {
  calculation_type: 'percentage',
};

const atLeastOneScheduledEventRule = {
  required: true,
  message: getTranslatedString('landlordPortal.incentives.atLeastOneScheduledEvent'),
};

const BenefitDrawer = ({ mode, drawer, addBenefit, initialValues = defaultInitialValues }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [benefitsForm] = Form.useForm();
  const incentive_type = Form.useWatch('incentive_type', benefitsForm);
  const schedule_type = Form.useWatch('schedule_type', benefitsForm);
  const calculation_type = Form.useWatch('calculation_type', benefitsForm);
  const [, scheduledEventsDrawer] = useDrawerState(false);
  const [scheduledEventsForm] = Form.useForm();
  const isMonthlyScheduleType = schedule_type === 'monthly';
  const isCustomScheduleType = schedule_type === 'custom';
  const isCalculationTypePercentage = calculation_type === 'percentage';
  const isGiftIncentiveType = incentive_type === 'gift';

  const getSubTypeOptions = (type) => {
    switch (type) {
      case 'discount':
      case 'deferral':
        return [
          { label: getLabel('monthly_rent'), value: 'monthly_rent' },
          { label: getLabel('application_fee'), value: 'application_fee' },
          { label: getLabel('parking'), value: 'parking' },
          { label: getLabel('pets'), value: 'pets' },
          { label: getLabel('storage'), value: 'storage' },
          { label: getLabel('last_months_rent'), value: 'last_months_rent' },
          { label: getLabel('security_deposit'), value: 'security_deposit' },
        ];
      case 'gift':
      default:
        return [];
    }
  };

  const handleDrawerClose = () => {
    benefitsForm.resetFields();
    drawer.close();
  };

  const handleScheduledEventsDrawerClose = () => {
    scheduledEventsForm.resetFields();
    scheduledEventsDrawer.close();
  };

  const onlyIntegerRule = {
    message: getTranslatedString('landlordPortal.incentives.onlyIntegersAreAllowed'),
    validator: (_, value) => (Number.isInteger(value) ? Promise.resolve() : Promise.reject()),
  };

  const renderCalculationType = () => (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.calculationType')}
      name="calculation_type"
      rules={[requiredRule]}
    >
      <Select
        options={[
          {
            value: 'percentage',
            label: getTranslatedString('landlordPortal.incentives.percentage'),
          },
          {
            value: 'absolute',
            label: getTranslatedString('landlordPortal.incentives.absolute'),
          },
        ]}
      />
    </Form.Item>
  );

  const renderMonthlyScheduleTypeFields = () => (
    <>
      <StyledRow isMobile={isMobile}>
        <Form.Item
          label={getTranslatedString('landlordPortal.incentives.startingMonth')}
          name="starting_month"
          rules={[requiredRule]}
        >
          <Select options={startingMonthOptions} />
        </Form.Item>

        <Form.Item
          label={getTranslatedString('landlordPortal.incentives.durationInMonths')}
          name="duration_in_months"
          rules={[requiredRule, onlyIntegerRule]}
        >
          <InputNumber min={MIN_DURATION_IN_MONTHS} max={MAX_DURATION_IN_MONTHS} />
        </Form.Item>
      </StyledRow>

      <StyledRow isMobile={isMobile}>
        {renderCalculationType()}

        {renderAmountPerMonth(isCalculationTypePercentage)}
      </StyledRow>
    </>
  );

  const renderCustomScheduleTypeFields = () => (
    <>
      {renderCalculationType()}

      <SectionTitle>
        {getTranslatedString('landlordPortal.incentives.scheduledEvents')}
      </SectionTitle>
      <Form.List name="scheduled_events" rules={[atLeastOneScheduledEventRule]}>
        {(fields, operations, { errors }) => {
          const addEvent = (event) => operations.add(event);

          return (
            <Space direction="vertical" size="middle">
              {fields.length ? (
                <ScheduledEventsTable
                  events={fields}
                  isCalculationTypePercentage={isCalculationTypePercentage}
                  operations={operations}
                />
              ) : null}

              <Button onClick={scheduledEventsDrawer.open}>
                {getTranslatedString('landlordPortal.incentives.addScheduledEvent')}
              </Button>

              <Form.ErrorList errors={errors} />

              <Drawer
                title={getTranslatedString('landlordPortal.incentives.addScheduledEvent')}
                visible={scheduledEventsDrawer.isVisible}
                closable
                destroyOnClose
                onClose={handleScheduledEventsDrawerClose}
              >
                <DrawerContent>
                  <Form
                    layout="vertical"
                    size={isMobile && 'large'}
                    form={scheduledEventsForm}
                    name="add-scheduled-event"
                    scrollToFirstError
                    onFinish={(values) => {
                      addEvent(values);
                      handleScheduledEventsDrawerClose();
                    }}
                    onFinishFailed={doNothing}
                  >
                    <StyledSpace direction="vertical" size="middle">
                      <Form.Item
                        label={getTranslatedString('landlordPortal.incentives.monthOfLease')}
                        name="month_of_lease"
                        rules={[requiredRule]}
                      >
                        <Select options={startingMonthOptions} />
                      </Form.Item>

                      {renderAmountPerMonth(isCalculationTypePercentage)}
                    </StyledSpace>

                    <Divider />

                    <Space direction="horizontal">
                      <Button type="primary" htmlType="submit">
                        {mode === 'create'
                          ? getTranslatedString('add')
                          : getTranslatedString('save')}
                      </Button>
                      <Button type="secondary" onClick={handleScheduledEventsDrawerClose}>
                        {getTranslatedString('landlordPortal.incentives.cancel')}
                      </Button>
                    </Space>
                  </Form>
                </DrawerContent>
              </Drawer>
            </Space>
          );
        }}
      </Form.List>
    </>
  );

  const drawerTitle =
    mode === 'create'
      ? getTranslatedString('landlordPortal.incentives.addABenefit')
      : `${getTranslatedString('landlordPortal.incentives.editABenefit')}`;

  return (
    <Drawer
      title={drawerTitle}
      visible={drawer.isVisible}
      closable={!scheduledEventsDrawer.isVisible}
      destroyOnClose={!scheduledEventsDrawer.isVisible}
      onClose={handleDrawerClose}
    >
      <DrawerContent>
        <Form
          layout="vertical"
          size={isMobile && 'large'}
          form={benefitsForm}
          name={`${mode}-incentive`}
          scrollToFirstError
          onFinish={(values) => {
            addBenefit(values);
            handleDrawerClose();
          }}
          initialValues={initialValues}
        >
          <StyledSpace direction="vertical" size="middle">
            <StyledRow isMobile={isMobile}>
              <Form.Item
                name="incentive_type"
                label={getTranslatedString('landlordPortal.incentives.incentiveType')}
                rules={[requiredRule]}
              >
                <Select
                  options={[
                    {
                      value: 'discount',
                      label: getTranslatedString('landlordPortal.incentives.discount'),
                    },
                    {
                      value: 'deferral',
                      label: getTranslatedString('landlordPortal.incentives.deferral'),
                    },
                    { value: 'gift', label: getTranslatedString('landlordPortal.incentives.gift') },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="sub_type"
                label={getTranslatedString('landlordPortal.incentives.incentiveSubtype')}
                rules={[!isGiftIncentiveType && requiredRule].filter(Boolean)}
              >
                <Select
                  options={getSubTypeOptions(incentive_type)}
                  disabled={!incentive_type || isGiftIncentiveType}
                />
              </Form.Item>
            </StyledRow>

            <Form.Item
              label={getTranslatedString('landlordPortal.incentives.scheduleType')}
              name="schedule_type"
              rules={[requiredRule]}
            >
              <Select
                options={[
                  {
                    value: 'monthly',
                    label: getTranslatedString('landlordPortal.incentives.monthly'),
                  },
                  {
                    value: 'custom',
                    label: getTranslatedString('landlordPortal.incentives.custom'),
                  },
                ]}
              />
            </Form.Item>

            {isMonthlyScheduleType && renderMonthlyScheduleTypeFields()}
            {isCustomScheduleType && renderCustomScheduleTypeFields()}
          </StyledSpace>

          <Divider />

          <Space direction="horizontal">
            <Button type="primary" htmlType="submit">
              {getTranslatedString('landlordPortal.incentives.add')}
            </Button>
            <Button type="secondary" onClick={handleDrawerClose}>
              {getTranslatedString('landlordPortal.incentives.cancel')}
            </Button>
          </Space>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default BenefitDrawer;
